import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { subscribeTick } from './kiteTicker';

// Split into two contexts on purpose: actions (stable references, rarely
// change) vs. state (basketItems/toast/prices/etc., changes often). A
// component that only calls an action — e.g. ltp_index.js's inline "+"
// button just needs addToBasket — should subscribe to ONLY the actions
// context, so it never re-renders just because a toast timer fired or a
// price finished loading elsewhere. Before this split, everything lived in
// one context object rebuilt on every provider render, so any unrelated
// state change forced every consumer (including the large option-chain
// table) to re-render — a real source of the sluggish "add to basket"
// click that got reported.
const OptionsBasketActionsContext = createContext();
const OptionsBasketStateContext = createContext();

export const OPTIONS_ACCOUNT = 'AWDHESH';

// Kite tradingsymbols are prefixed by underlying (NIFTY..., SENSEX...,
// BANKNIFTY...), so a prefix check is enough to size the default SL without
// needing the caller to separately track/pass which underlying a leg is from.
// 'NIFTY' correctly excludes BANKNIFTY (prefixed 'BANK', not 'NIFTY').
export const DEFAULT_SL_POINTS_BY_UNDERLYING = { NIFTY: 10, SENSEX: 25 };
export const getDefaultSlPoints = (tradingsymbol = '') => {
    if (tradingsymbol.startsWith('SENSEX')) return DEFAULT_SL_POINTS_BY_UNDERLYING.SENSEX;
    if (tradingsymbol.startsWith('NIFTY')) return DEFAULT_SL_POINTS_BY_UNDERLYING.NIFTY;
    return '';
};

// Shared by every page that has a "which index is currently open" concept
// (ltp_index.js, indices.js) so a quick-add popup launched from that page can
// default straight to the right underlying instead of starting blank.
export const TICKER_TO_UNDERLYING = {
    NIFTY: { underlying: 'NIFTY', exchange: 'NFO' },
    'NIFTY BANK': { underlying: 'BANKNIFTY', exchange: 'NFO' },
    SENSEX: { underlying: 'SENSEX', exchange: 'BFO' },
};

// Indices aren't quotable under their own tradingsymbol — Kite lists them
// under the exchange's index feed instead (e.g. "NIFTY" the underlying vs.
// "NIFTY 50" the spot quote). Anything not in this map is assumed to be a
// single-stock underlying, quotable directly on NSE. Shared by any picker
// that needs a reference spot price for "nearest to spot" strikes.
export const SPOT_QUOTE_SYMBOL = {
    NIFTY: { exchange: 'NSE', tradingsymbol: 'NIFTY 50' },
    BANKNIFTY: { exchange: 'NSE', tradingsymbol: 'NIFTY BANK' },
    SENSEX: { exchange: 'BSE', tradingsymbol: 'SENSEX' },
};

export const OPTIONS_API_BASE = 'https://live1.finstrats.com';

// Server-backed re-entry momentum for closed positions — deliberately NOT
// built from each browser tab's own live-tick history, since that's local
// to whichever device happens to have the tab open and how long it's been
// open, giving a different (or entirely blank) number on every device.
// Nifty_option_data_3min is one shared backend table, updated every 3
// minutes, so this is the same number everywhere. Cached briefly per
// ticker-set since the data itself only changes every 3 minutes anyway.
const _momentumCache = { key: null, promise: null, timestamp: 0 };
const MOMENTUM_CACHE_TTL_MS = 30 * 1000;

export const fetchMomentum = (tickerCodes) => {
    const codes = [...new Set((tickerCodes || []).filter(Boolean))].sort();
    if (codes.length === 0) return Promise.resolve({});
    const key = codes.join(',');
    if (_momentumCache.key === key && Date.now() - _momentumCache.timestamp < MOMENTUM_CACHE_TTL_MS) {
        return _momentumCache.promise;
    }
    const promise = fetch(`${OPTIONS_API_BASE}/portfolio/momentum?tickers=${codes.map(encodeURIComponent).join(',')}`)
        .then(res => res.json());
    promise.catch(() => { _momentumCache.key = null; });
    _momentumCache.key = key;
    _momentumCache.promise = promise;
    _momentumCache.timestamp = Date.now();
    return promise;
};

// candles: [{t: "YYYY-MM-DD HH:MM:SS", close}, ...] oldest-first, as
// returned by /portfolio/momentum — used only to look up the reference price
// from ~minutesAgo ago (the one thing that needs to be a shared, consistent
// value across devices). The "now" side is the live websocket tick price
// (currentPrice), not the DB's own latest candle, so momentum stays exactly
// as real-time as the LTP column instead of lagging to the 3-minute candle
// cadence. Returns null if there isn't yet enough real history in the table
// to cover the requested window (rather than silently comparing against
// whatever the oldest available point happens to be, which would understate
// the window without saying so).
export const computeMomentumPct = (candles, minutesAgo, currentPrice) => {
    if (!candles || candles.length === 0 || currentPrice === null || currentPrice === undefined) return null;
    const toMs = (t) => new Date(t.replace(' ', 'T')).getTime();
    const cutoff = Date.now() - minutesAgo * 60 * 1000;
    if (toMs(candles[0].t) > cutoff) return null;
    let refClose = candles[0].close;
    for (let i = 0; i < candles.length; i++) {
        if (toMs(candles[i].t) >= cutoff) { refClose = candles[i].close; break; }
    }
    if (!refClose) return null;
    return ((currentPrice - refClose) / refClose) * 100;
};

// Basket survives a page reload, but option contracts expire daily/weekly —
// a leg saved on an earlier calendar day could be an expired or wrong-day
// contract, so a stale blob is discarded rather than restored.
const BASKET_STORAGE_KEY = 'finstrats_options_basket_v1';

const loadStoredBasket = () => {
    try {
        const raw = localStorage.getItem(BASKET_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (parsed.savedOn !== new Date().toDateString()) return [];
        return Array.isArray(parsed.items) ? parsed.items : [];
    } catch (err) {
        return [];
    }
};

// NSE/BSE F&O session: weekdays, 09:15-15:40 IST. Outside this window a
// 'regular' order is simply rejected by the broker, so orders placed off-hours
// go out as AMO instead (queued broker-side, fired at the next session open).
export const isMarketOpen = () => {
    const now = new Date();
    const day = now.getDay();
    if (day < 1 || day > 5) return false;
    const minutes = now.getHours() * 60 + now.getMinutes();
    return minutes >= (9 * 60 + 15) && minutes <= (15 * 60 + 40);
};

export const OptionsBasketProvider = ({ children }) => {
    const [basketItems, setBasketItems] = useState(loadStoredBasket);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isPlacing, setIsPlacing] = useState(false);
    const [placeResults, setPlaceResults] = useState(null);
    const [toast, setToast] = useState({ show: false, message: '', type: '' });
    const [prices, setPrices] = useState({}); // tradingsymbol -> last_price
    // How many OptionsBasketPanel instances are currently mounted — the panel
    // renders both inside the overlay drawer AND directly on the /order-basket
    // page, so "should prices be polling" can no longer be tied to isDrawerOpen
    // specifically; it just needs at least one panel actually on screen.
    const [activePanelCount, setActivePanelCount] = useState(0);
    const registerPanelMounted = useCallback(() => setActivePanelCount(c => c + 1), []);
    const registerPanelUnmounted = useCallback(() => setActivePanelCount(c => Math.max(0, c - 1)), []);

    // Lets placeAll read the current basket at call-time without needing
    // basketItems in its own dependency array — see the note above placeAll
    // for why that matters (keeping every action reference-stable).
    const basketItemsRef = useRef(basketItems);
    useEffect(() => { basketItemsRef.current = basketItems; }, [basketItems]);

    // Same pattern, for placeAll to fall back a blank LIMIT price to the
    // current live LTP at the moment of firing instead of blocking the order.
    const pricesRef = useRef(prices);
    useEffect(() => { pricesRef.current = prices; }, [prices]);

    useEffect(() => {
        if (toast.show) {
            const timer = setTimeout(() => setToast({ show: false, message: '', type: '' }), 5000);
            return () => clearTimeout(timer);
        }
    }, [toast.show]);

    // Persist on every change, stamped with today's date so a future load can
    // tell a same-day basket (safe to restore) from a stale one (discarded by
    // loadStoredBasket above).
    useEffect(() => {
        try {
            localStorage.setItem(BASKET_STORAGE_KEY, JSON.stringify({ savedOn: new Date().toDateString(), items: basketItems }));
        } catch (err) {
            // localStorage unavailable/full — basket still works in-memory for this tab.
        }
    }, [basketItems]);

    // While a basket panel is actually on screen (drawer overlay or the
    // /order-basket page), LTP is the number being decided against — premium
    // can move meaningfully in the few seconds it takes to review a multi-leg
    // basket, so it needs to stay current. Subscribed via the same live Kite
    // ticker websocket as OptionChainPicker.js (kiteTicker.js) rather than
    // REST-polling /portfolio/quotes on an interval. Stops the moment no
    // panel is visible — no reader for `prices` anywhere else, no point
    // holding subscriptions open in the background.
    //
    // Keyed off a derived (tradingsymbol:token) key, not basketItems itself —
    // basketItems gets a new array/object reference on every field edit
    // (price, qty, product…), which would otherwise tear down and rebuild
    // every subscription on each keystroke. This only changes when the set
    // of distinct legs actually changes (add/remove).
    const basketLegsKey = basketItems.map(i => `${i.tradingsymbol}:${i.instrument_token || ''}`).sort().join(',');
    const basketLegsForTicker = useMemo(() => {
        const seen = new Set();
        return basketItems.filter(item => {
            if (!item.instrument_token || seen.has(item.tradingsymbol)) return false;
            seen.add(item.tradingsymbol);
            return true;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [basketLegsKey]);

    useEffect(() => {
        if (activePanelCount === 0 || basketLegsForTicker.length === 0) return;
        const unsubscribers = basketLegsForTicker.map(item => subscribeTick(item.instrument_token, (price) => {
            setPrices(prev => (prev[item.tradingsymbol] === price ? prev : { ...prev, [item.tradingsymbol]: price }));
        }));
        return () => unsubscribers.forEach(unsub => unsub());
    }, [activePanelCount, basketLegsForTicker]);

    // A leg just switched to LIMIT starts with a blank price (edited via the
    // basket panel's Price input) — once the poll above resolves a live LTP
    // for a leg that's still blank, seed it as a starting point rather than
    // leaving the field empty. Bails out via the same-reference return when
    // nothing needs seeding, so this doesn't fight the poll effect above by
    // replacing basketItems on every tick.
    useEffect(() => {
        setBasketItems(prev => {
            let changed = false;
            const next = prev.map(item => {
                if (item.order_type === 'LIMIT' && !item.price && prices[item.tradingsymbol] !== undefined) {
                    changed = true;
                    return { ...item, price: prices[item.tradingsymbol] };
                }
                return item;
            });
            return changed ? next : prev;
        });
    }, [prices]);

    const notify = useCallback((message, type = 'success') => setToast({ show: true, message, type }), []);

    const addToBasket = useCallback((contract, transactionType = 'SELL', product = 'MIS', { noSl = false } = {}) => {
        setBasketItems(prev => {
            const exists = prev.find(item => item.tradingsymbol === contract.tradingsymbol && item.transaction_type === transactionType);
            if (exists) {
                notify(`${contract.tradingsymbol} (${transactionType}) already in basket`, 'error');
                return prev;
            }
            notify(`Added ${contract.tradingsymbol} (${transactionType}) to basket`, 'success');
            return [...prev, {
                id: `${contract.tradingsymbol}-${transactionType}-${Date.now()}`,
                tradingsymbol: contract.tradingsymbol,
                exchange: contract.exchange,
                strike: contract.strike,
                instrument_type: contract.instrument_type,
                expiry: contract.expiry,
                lot_size: contract.lot_size,
                instrument_token: contract.instrument_token,
                transaction_type: transactionType,
                lots: 1,
                product,
                order_type: 'LIMIT',
                price: '',
                sl_points: noSl ? '' : getDefaultSlPoints(contract.tradingsymbol),
                no_sl: noSl,
            }];
        });
    }, [notify]);

    const removeFromBasket = useCallback((id) => {
        setBasketItems(prev => prev.filter(item => item.id !== id));
    }, []);

    const updateItem = useCallback((id, field, value) => {
        setBasketItems(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
    }, []);

    const clearBasket = useCallback(() => {
        setBasketItems([]);
        setPlaceResults(null);
        notify('Basket cleared', 'success');
    }, [notify]);

    // Shared by the batch "Place All" (basket drawer) and the single-leg quick
    // order popup — both are just a 1-or-more-item order to the same endpoint.
    // /order/place-option-basket has no AMO support server-side (placeOptionOrderWithSL
    // hardcodes variety='regular'), unlike /order/place[-all], so outside market
    // hours this would just fire a regular order the broker instantly rejects —
    // refuse client-side instead of making that call at all.
    const submitOrders = useCallback(async (orderList) => {
        if (!isMarketOpen()) {
            return { status: 'error', results: [], errors: orderList.map(o => ({ symbol: o.tradingsymbol, error: 'Market is closed — option orders are not accepted outside 09:15-15:40 IST' })) };
        }
        const payload = { account: OPTIONS_ACCOUNT, orders: orderList };
        const res = await fetch(`${OPTIONS_API_BASE}/portfolio/order/place-option-basket`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        return res.json();
    }, []);

    // Reads basketItemsRef rather than taking basketItems as a dependency —
    // that keeps placeAll (and therefore the whole actions bundle below)
    // reference-stable across basket edits. Side effects (the actual order
    // placement) stay inside a plain async function, never inside a setState
    // updater, which React can invoke more than once.
    // `selectedIds` (optional Set) lets the basket panel's checkboxes place
    // only the checked legs — omitted (e.g. the Ctrl/Cmd+Enter shortcut),
    // this places the whole basket.
    const placeAll = useCallback(async (selectedIds) => {
        const currentItems = selectedIds ? basketItemsRef.current.filter(i => selectedIds.has(i.id)) : basketItemsRef.current;
        if (currentItems.length === 0) {
            notify('Add option contracts to basket first', 'error');
            return;
        }
        // A LIMIT leg left with no price fires at the current live LTP
        // instead of blocking the order — only a leg with truly no LTP yet
        // (ticker hasn't delivered one at all) can't be resolved either way.
        const resolvedItems = currentItems.map(item => {
            if (item.order_type === 'LIMIT' && !(parseFloat(item.price) > 0)) {
                const ltp = pricesRef.current[item.tradingsymbol];
                if (ltp !== undefined) return { ...item, price: ltp };
            }
            return item;
        });
        const missingPrice = resolvedItems.find(item => item.order_type === 'LIMIT' && !(parseFloat(item.price) > 0));
        if (missingPrice) {
            notify(`${missingPrice.tradingsymbol} has no live price yet — wait a moment and try again`, 'error');
            return;
        }
        setIsPlacing(true);
        setPlaceResults(null);
        try {
            const result = await submitOrders(resolvedItems.map(orderPayloadFor));
            setPlaceResults(result);
            const placedCount = result?.results?.length || 0;
            const failedCount = result?.errors?.length || 0;
            notify(`${placedCount} placed, ${failedCount} failed`, failedCount > 0 ? 'error' : 'success');
        } catch (err) {
            notify(err.message || 'Connection error. Is the server running?', 'error');
        } finally {
            setIsPlacing(false);
        }
    }, [notify, submitOrders]);

    // Used by the quick-order popup for an immediate single-leg order — same
    // endpoint/leg shape as the basket, just a one-item list, and the caller
    // handles its own loading/result state rather than the shared basket one.
    const placeSingle = useCallback(async (leg) => {
        const result = await submitOrders([orderPayloadFor(leg)]);
        if (result?.errors?.length > 0 && (!result?.results || result.results.length === 0)) {
            throw new Error(result.errors[0].error || 'Order failed');
        }
        return result;
    }, [submitOrders]);

    const actions = useMemo(() => ({
        addToBasket, removeFromBasket, updateItem, clearBasket, placeAll, placeSingle, setIsDrawerOpen, notify,
        registerPanelMounted, registerPanelUnmounted,
    }), [addToBasket, removeFromBasket, updateItem, clearBasket, placeAll, placeSingle, notify, registerPanelMounted, registerPanelUnmounted]);

    const state = useMemo(() => ({
        basketItems, isPlacing, placeResults, isDrawerOpen, toast, prices,
    }), [basketItems, isPlacing, placeResults, isDrawerOpen, toast, prices]);

    return (
        <OptionsBasketActionsContext.Provider value={actions}>
            <OptionsBasketStateContext.Provider value={state}>
                {children}
            </OptionsBasketStateContext.Provider>
        </OptionsBasketActionsContext.Provider>
    );
};

// Lot size differs per underlying (NIFTY/BANKNIFTY/SENSEX all differ, and
// Kite can change them on expiry) — users think and pick in lots, the API
// wants raw quantity, so the lots -> quantity multiply happens right here.
// sl_points defaults per-underlying (getDefaultSlPoints, 10/25 for NIFTY/
// SENSEX) at add-to-basket time but is editable per leg from there — sent
// through so a user override actually reaches placeOptionOrderWithSL rather
// than being silently discarded; omitted when left blank so the server's
// own default applies. Product/variety are still predefined server-side.
const orderPayloadFor = (leg) => {
    const payload = {
        tradingsymbol: leg.tradingsymbol,
        exchange: leg.exchange,
        transaction_type: leg.transaction_type,
        quantity: (parseInt(leg.lots) || 1) * leg.lot_size,
        order_type: leg.order_type,
        price: leg.order_type === 'MARKET' ? undefined : parseFloat(leg.price || 0),
        sl_points: leg.sl_points !== '' && leg.sl_points !== undefined && leg.sl_points !== null
            ? parseFloat(leg.sl_points)
            : undefined,
    };
    // Blank sl_points alone means "use the server default SL", so a leg that
    // must have no SL at all says so explicitly.
    if (leg.no_sl) payload.no_sl = true;
    if (leg.product) payload.product = leg.product;
    return payload;
};

// Kite's own order-margin calculator (basket_order_margins) — accounts for
// hedge/netting benefit across the legs, unlike a naive premium-times-qty
// sum. `orders[i].total` is the per-leg figure; `initial.total`/`final.total`
// are the combined basket numbers shown in the drawer footer.
export const fetchBasketMargins = async (items) => {
    if (!items || items.length === 0) return { initial: { total: 0 }, final: { total: 0 }, orders: [] };
    const res = await fetch(`${OPTIONS_API_BASE}/portfolio/order/basket-margins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account: OPTIONS_ACCOUNT, orders: items.map(orderPayloadFor) }),
    });
    const data = await res.json();
    if (res.ok && data.status === 'success') return data.data;
    throw new Error(data.message || 'Failed to fetch basket margins');
};

// Shared helpers for any option-chain page that wants to resolve a real,
// order-ready Kite tradingsymbol for a given strike (chain pages typically
// only carry strike/CE-PE/LTP, not the exact tradingsymbol+expiry).
export const fetchOptionChain = async (exchange, underlying) => {
    const res = await fetch(`${OPTIONS_API_BASE}/portfolio/options/instruments?exchange=${exchange}&underlying=${encodeURIComponent(underlying)}`);
    const data = await res.json();
    if (res.ok && data.status === 'success') return data.data;
    throw new Error(data.message || 'Failed to load option chain');
};

// Resolves a spot index's instrument_token (e.g. NSE:"NIFTY 50") for subscribing
// on the live Kite ticker websocket — option legs already carry their own
// instrument_token from fetchOptionChain, but the spot quote doesn't. Cached
// indefinitely per session since a symbol's token never changes intraday.
const _instrumentTokenCache = {};
export const fetchInstrumentToken = async (exchange, tradingsymbol) => {
    const key = `${exchange}:${tradingsymbol}`;
    if (_instrumentTokenCache[key]) return _instrumentTokenCache[key];
    const promise = fetch(`${OPTIONS_API_BASE}/portfolio/instrument_token?exchange=${exchange}&tradingsymbol=${encodeURIComponent(tradingsymbol)}`)
        .then(res => res.json())
        .then(data => {
            if (data.instrument_token == null) throw new Error(data.error || 'instrument_token not found');
            return data.instrument_token;
        });
    promise.catch(() => { delete _instrumentTokenCache[key]; });
    _instrumentTokenCache[key] = promise;
    return promise;
};

// Module-level (not React state) cache for the "fast add" picker — the whole
// point of that picker is speed, and the instrument-list + spot-quote fetch
// it needs is the slow part. A page that knows it's about to be opened (e.g.
// the Option Basket page, the moment its route is entered) can warm this via
// prefetchOptionChainFor() so the picker's own fetch is already-cached by the
// time it opens; any caller can also just call getOptionChainCached directly
// and get the same warm-cache benefit regardless of entry point.
//
// Caches the in-flight PROMISE, not just the resolved result — a naive
// "cache after it resolves" leaves a gap where a prefetch that's still in
// flight (the realistic case: the instrument-list fetch alone can take a
// couple of seconds on a cold backend cache) gets completely ignored by a
// caller that shows up before it finishes, kicking off a second, redundant
// fetch instead of just awaiting the first one. Caching the promise means
// every caller within the TTL — prefetch or the picker itself — shares
// exactly one network round trip.
const _chainCache = {}; // underlying -> { promise, timestamp }
const CHAIN_CACHE_TTL_MS = 60 * 1000;

export const getOptionChainCached = (exchange, underlying) => {
    const cached = _chainCache[underlying];
    if (cached && Date.now() - cached.timestamp < CHAIN_CACHE_TTL_MS) return cached.promise;

    const spotContract = SPOT_QUOTE_SYMBOL[underlying] || { exchange: 'NSE', tradingsymbol: underlying };
    // Chain and spot quote don't depend on each other — fetched in parallel
    // rather than one-after-the-other to shave a full round trip off the wait.
    const promise = Promise.all([
        fetchOptionChain(exchange, underlying),
        fetchQuotes([spotContract]),
    ]).then(([chain, spotPrices]) => ({
        chain,
        spotPrice: spotPrices[spotContract.tradingsymbol] ?? null,
    }));

    // A failed fetch shouldn't poison the cache for the next 60s — clear the
    // entry so the next caller (prefetch or manual retry) gets a fresh attempt.
    promise.catch(() => { delete _chainCache[underlying]; });

    _chainCache[underlying] = { promise, timestamp: Date.now() };
    return promise;
};

// Fire-and-forget warm-up — callers that don't need the result (just want the
// cache populated ahead of time) can ignore the returned promise.
export const prefetchOptionChainFor = (tickerKey) => {
    const map = TICKER_TO_UNDERLYING[tickerKey];
    if (!map) return;
    getOptionChainCached(map.exchange, map.underlying).catch(() => {});
};

// Contracts come back sorted by (expiry, strike, instrument_type) — the first
// match for a given strike/type is the nearest expiry, which is what a live
// chain view (no expiry selector) implicitly means by "the strike's price".
export const resolveNearestContract = (chain, strike, instrumentType) => {
    const target = Number(strike);
    return chain.find(c => Number(c.strike) === target && c.instrument_type === instrumentType) || null;
};

// Shared by any "nearest to spot" picker (AddContractModal, QuickAddChainModal)
// — the nearest expiry's strikes closest to the reference spot price, grouped
// one row per strike holding both legs (Call | Strike | Put), plus which
// strike is closest to spot (ATM) so the caller can highlight/scroll to it.
// Falls back to the chain's middle strike as the reference if spot didn't
// resolve, rather than showing nothing.
export const groupNearestStrikes = (chain, spotPrice, count) => {
    if (!chain || chain.length === 0) return null;
    const nearestExpiry = chain[0].expiry;
    const sameExpiry = chain.filter(c => c.expiry === nearestExpiry);
    const uniqueStrikes = [...new Set(sameExpiry.map(c => c.strike))].sort((a, b) => a - b);
    const ref = spotPrice !== null && spotPrice !== undefined ? spotPrice : uniqueStrikes[Math.floor(uniqueStrikes.length / 2)];
    const nearestStrikes = new Set(
        [...uniqueStrikes].sort((a, b) => Math.abs(a - ref) - Math.abs(b - ref)).slice(0, count)
    );
    const contracts = sameExpiry
        .filter(c => nearestStrikes.has(c.strike))
        .sort((a, b) => a.strike - b.strike || a.instrument_type.localeCompare(b.instrument_type));

    const byStrike = {};
    contracts.forEach(c => {
        if (!byStrike[c.strike]) byStrike[c.strike] = { strike: c.strike, ce: null, pe: null };
        byStrike[c.strike][c.instrument_type.toLowerCase()] = c;
    });
    const rows = Object.values(byStrike).sort((a, b) => a.strike - b.strike);
    let atmStrike = null;
    let atmDist = Infinity;
    rows.forEach(r => {
        const dist = Math.abs(r.strike - ref);
        if (dist < atmDist) { atmDist = dist; atmStrike = r.strike; }
    });

    return { expiry: nearestExpiry, contracts, rows, atmStrike };
};

// Reuses the existing /portfolio/quotes endpoint (already used by BasketOrders.js
// for equities) to get a reference LTP before placing — neither the instrument
// master nor the analytics chain tables carry a live price. Returns a plain
// { tradingsymbol: last_price } map so callers don't need to know the
// EXCHANGE:SYMBOL key format the quotes endpoint itself uses.
export const fetchQuotes = async (contracts) => {
    if (!contracts || contracts.length === 0) return {};
    const symbols = contracts.map(c => `${c.exchange}:${c.tradingsymbol}`).join(',');
    try {
        const res = await fetch(`${OPTIONS_API_BASE}/portfolio/quotes?symbols=${encodeURIComponent(symbols)}`);
        const data = await res.json();
        const next = {};
        contracts.forEach(c => {
            const quote = data[`${c.exchange}:${c.tradingsymbol}`];
            if (quote) next[c.tradingsymbol] = quote.last_price;
        });
        return next;
    } catch (err) {
        return {};
    }
};

// OI change (delta) isn't something Kite's live quote API exposes — its quote
// response has a raw `oi` snapshot but no prior reference to diff against.
// The analytics chain endpoint (/ltp_data, same one option_chain_pro.js's
// live table uses) already computes ce_oi_change/pe_oi_change server-side per
// strike from its own snapshot tables, keyed by ticker ('NIFTY'/'SENSEX') —
// reused here rather than re-deriving OI change on the frontend. Returns a
// { strike: { CE: change, PE: change } } map so callers can look up by the
// same numeric strike the instrument list already carries.
export const fetchOiChangeByStrike = async (ticker) => {
    try {
        const res = await fetch(`${OPTIONS_API_BASE}/ltp_data?ticker=${encodeURIComponent(ticker)}`);
        const data = await res.json();
        const map = {};
        (Array.isArray(data) ? data : []).forEach(row => {
            if (row.strikes !== undefined) {
                map[Number(row.strikes)] = { CE: row.ce_oi_change, PE: row.pe_oi_change };
            }
        });
        return map;
    } catch (err) {
        return {};
    }
};

// Narrow subscription — use this in any component that only fires actions
// (addToBasket, etc.) and never reads basket state. Its value only changes
// if the provider itself remounts, so it will not cause re-renders when
// toast/prices/basketItems change elsewhere.
export const useOptionsBasketActions = () => {
    const context = useContext(OptionsBasketActionsContext);
    if (!context) {
        throw new Error('useOptionsBasketActions must be used within an OptionsBasketProvider');
    }
    return context;
};

// Narrow subscription for components that only read state (rare — most that
// need state also need an action, so useOptionsBasket() below covers them).
export const useOptionsBasketState = () => {
    const context = useContext(OptionsBasketStateContext);
    if (!context) {
        throw new Error('useOptionsBasketState must be used within an OptionsBasketProvider');
    }
    return context;
};

// Combined convenience hook for components that genuinely need both (e.g.
// OptionsBasketDrawer, which reads basketItems/toast/prices AND calls
// removeFromBasket/placeAll/etc.) — subscribes to both contexts, so it
// re-renders on any relevant change, same as before the split.
export const useOptionsBasket = () => ({ ...useOptionsBasketActions(), ...useOptionsBasketState() });

export default OptionsBasketActionsContext;
